/**
 * The company indicator in the top bar.
 *
 * ── What it does for a user who belongs to one company (ADR-002 §4.5) ──────
 *
 * **It is not a control.** One company means there is nothing to switch to, so the
 * name renders as plain text with a description of why it cannot be changed. It is
 * deliberately *not* a disabled dropdown: a disabled control says "this could do
 * something, but not for you", which is false — there is no other company, and the
 * greyed chevron would send a reader looking for the permission they are missing.
 * A static label says the true thing, and it is the same shape for every one of the
 * sixteen screens that inherit this frame.
 *
 * ── And for a user who belongs to several ──────────────────────────────────
 *
 * `POST /api/v1/auth/switch-company` is specified in `openapi.yaml` and **is not
 * mounted** — `src/modules/auth/routes.ts` says so at the top of the file, and says
 * why: switching writes `wp_audit_log` and has to check `wp_companies.enabled`. So
 * there is no request this component could make. It renders the count and a link to
 * `/select-company`, which explains the state in full (`web/shapes/select-company.md`).
 *
 * When that endpoint lands this becomes a menu of buttons and nothing else here
 * changes: the memberships, their `enabled` flags and the copy are already right.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { useT } from '../i18n';
import type { SessionUser } from '../api/types';
import { Icon } from '../states/icons';

export interface CompanySwitcherProps {
  readonly user: SessionUser;
}

export function CompanySwitcher({ user }: CompanySwitcherProps): ReactNode {
  const t = useT();
  const active = user.companies.find((c) => c.company_id === user.active_company_id) ?? null;
  const multiple = user.companies.length > 1;

  return (
    <div className="wp-company" data-testid="company-switcher">
      <span className="wp-visually-hidden">{t('shell.company')}: </span>
      <span className="wp-company__name">{active?.name ?? '—'}</span>
      {multiple ? (
        <Link className="wp-small" to="/select-company" data-testid="company-switch-link">
          {t('shell.companyCount', { count: user.companies.length })}
        </Link>
      ) : (
        // Static, not disabled. See the module comment.
        <span className="wp-small wp-muted wp-company__note" data-testid="company-single">
          <Icon name="lock" /> {t('shell.companyOnly')}
        </span>
      )}
    </div>
  );
}
